// web/src/admin/AdminLeads.tsx — /admin/leads: free-preview requests (portal spec 24). KC reads who asked and why, marks
// "Start building", builds the demo outside the dashboard, then "Attach demo" (slug, name, preview URL, repo URL) — the
// prospect sees it and the "preview ready" email waits in the Outbox for KC. Spec 22: in the operator nav with a badge
// (new leads), and an attached demo links to its client page.
import { useMemo, useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import type { AdminLead, PreviewLeadStatus } from '../lib/types';
import { apiErrorText } from '../lib/errors';
import { PageHeader } from '../components/PageHeader';
import { Icon } from '../components/Icon';
import { Modal } from '../components/Modal';
import { ConfirmDialog } from '../components/ConfirmDialog';
import { toast } from '../components/Toast';

const CHIPS: { s: PreviewLeadStatus; label: string }[] = [
  { s: 'new', label: 'New' }, { s: 'building', label: 'Building' }, { s: 'demo_ready', label: 'Demo ready' },
  { s: 'sold', label: 'Sold' }, { s: 'declined', label: 'Declined' },
];
const LABEL = Object.fromEntries(CHIPS.map((c) => [c.s, c.label])) as Record<PreviewLeadStatus, string>;
const TONE: Record<PreviewLeadStatus, string> = {
  new: 'border-info text-info', building: 'border-warn text-warn', demo_ready: 'border-run text-run',
  sold: 'border-run bg-run/10 text-run', declined: 'border-hair text-muted',
};
const OPEN: PreviewLeadStatus[] = ['new', 'building', 'demo_ready'];
const web = (u: string | null | undefined) => (u && /^https?:\/\//i.test(u) ? u : null);
const host = (u: string) => { try { return new URL(u).host.replace(/^www\./, '') + new URL(u).pathname.replace(/\/$/, ''); } catch { return u; } };
const slugify = (s: string) => s.toLowerCase().normalize('NFKD').replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 50);
function age(iso: string): string {
  const h = (Date.now() - Date.parse(iso)) / 3_600_000;
  if (h < 1) return 'just now';
  if (h < 24) return `${Math.floor(h)} h ago`;
  const d = Math.floor(h / 24);
  return d === 1 ? 'yesterday' : `${d} days ago`;
}

export function AdminLeads() {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['admin-leads'], queryFn: () => api.adminLeads() });
  const [show, setShow] = useState<Set<PreviewLeadStatus>>(new Set(['new', 'building']));
  const [declining, setDeclining] = useState<AdminLead | null>(null);
  const [attaching, setAttaching] = useState<AdminLead | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const all = q.data ?? [];
  const rows = useMemo(() => all.filter((r) => show.has(r.l.status)), [all, show]);
  const count = (s: PreviewLeadStatus) => all.filter((r) => r.l.status === s).length;
  const toggle = (s: PreviewLeadStatus) => setShow((cur) => { const n = new Set(cur); if (n.has(s)) n.delete(s); else n.add(s); return n; });

  const move = async (r: AdminLead, status: 'new' | 'building' | 'declined') => {
    setBusy(r.l.id);
    try {
      await api.adminLeadStatus(r.l.id, status);
      toast({ kind: 'success', message: status === 'building' ? `${r.l.businessName}: building.` : status === 'declined' ? `${r.l.businessName}: declined.` : `${r.l.businessName}: back to new.` });
      void qc.invalidateQueries({ queryKey: ['admin-leads'] });
    } catch (e) {
      toast({ kind: 'error', message: apiErrorText(e, 'Couldn’t change that lead.') });
    } finally { setBusy(null); setDeclining(null); }
  };

  return (
    <>
      <PageHeader title="Leads" subtitle="Free-preview requests. Start building, attach the demo you built, or decline." />
      <div className="mb-4 flex flex-wrap gap-2" role="group" aria-label="Show leads by status">
        {CHIPS.map((c) => (
          <button key={c.s} type="button" onClick={() => toggle(c.s)} aria-pressed={show.has(c.s)}
            className={`inline-flex min-h-11 items-center gap-1.5 rounded-full border px-3 text-sm ${show.has(c.s) ? 'border-run bg-run/10 text-ink' : 'border-hair text-muted'}`}>
            {c.label}<span className="text-xs text-muted">{count(c.s)}</span>
          </button>
        ))}
      </div>

      {q.isLoading && <div className="h-40 animate-pulse rounded-lg bg-surface" />}
      {q.isError && <p className="rounded-lg border border-hair bg-surface/40 px-4 py-6 text-center text-sm text-muted">{apiErrorText(q.error, 'Couldn’t load the leads.')}</p>}
      {q.data && rows.length === 0 && (
        <p className="rounded-lg border border-hair bg-surface/40 px-4 py-6 text-center text-sm text-muted">
          {all.length ? 'Nothing with these statuses.' : 'No free-preview requests yet.'}
        </p>
      )}

      <div className="flex min-w-0 flex-col gap-3">
        {rows.map((r) => {
          const l = r.l;
          const site = web(l.currentSiteUrl);
          return (
            <article key={l.id} className="min-w-0 rounded-lg border border-hair bg-surface p-4">
              <div className="flex flex-wrap items-start justify-between gap-2">
                <h2 className="min-w-0 break-words font-head text-ink">{l.businessName}</h2>
                <span className={`shrink-0 rounded-full border px-2 py-0.5 text-xs ${TONE[l.status]}`}>{LABEL[l.status]}</span>
              </div>
              <p className="mt-0.5 text-xs text-muted">{age(l.createdAt)}</p>
              <dl className="mt-3 grid gap-1.5 text-sm">
                <div className="flex min-w-0 gap-2"><dt className="w-16 shrink-0 text-muted">Email</dt>
                  <dd className="min-w-0 break-all"><a className="text-ink underline" href={`mailto:${l.email}`}>{l.email}</a></dd></div>
                {l.phone && <div className="flex min-w-0 gap-2"><dt className="w-16 shrink-0 text-muted">Phone</dt>
                  <dd><a className="text-ink underline" href={`tel:${l.phone.replace(/[^0-9+]/g, '')}`}>{l.phone}</a></dd></div>}
                <div className="flex min-w-0 gap-2"><dt className="w-16 shrink-0 text-muted">Site</dt>
                  <dd className="min-w-0 break-all">{site
                    ? <a className="inline-flex items-center gap-1 text-ink underline" href={site} target="_blank" rel="noopener noreferrer nofollow">{host(site)}<Icon name="external" size={13} /></a>
                    : <span className="text-muted">none</span>}</dd></div>
                {l.socials.length > 0 && <div className="flex min-w-0 gap-2"><dt className="w-16 shrink-0 text-muted">Socials</dt>
                  <dd className="flex min-w-0 flex-col gap-0.5 break-all">{l.socials.map((s) => web(s)
                    ? <a key={s} className="text-ink underline" href={s} target="_blank" rel="noopener noreferrer nofollow">{host(s)}</a>
                    : <span key={s}>{s}</span>)}</dd></div>}
              </dl>
              <p className="mt-3 whitespace-pre-wrap break-words rounded-md bg-bg px-3 py-2 text-sm text-ink">{l.biggestIssue}</p>
              {r.siteSlug && (
                <p className="mt-3 text-sm text-muted">Demo: <Link className="text-ink underline" to={`/admin/clients/${encodeURIComponent(r.siteSlug)}`}>{r.siteSlug}</Link>
                  {r.siteStage && r.siteStage !== 'demo' ? ` · ${r.siteStage}` : ''}</p>
              )}
              {OPEN.includes(l.status) && (
                <div className="mt-4 flex flex-wrap gap-2">
                  {l.status === 'new' && <button type="button" disabled={busy === l.id} onClick={() => void move(r, 'building')}
                    className="inline-flex min-h-11 items-center gap-2 rounded-md bg-run px-4 text-sm font-medium text-runink disabled:opacity-50"><Icon name="flask" size={15} />Start building</button>}
                  {l.status === 'building' && <button type="button" disabled={busy === l.id} onClick={() => void move(r, 'new')}
                    className="inline-flex min-h-11 items-center rounded-md border border-hair px-4 text-sm text-ink disabled:opacity-50">Back to new</button>}
                  <button type="button" onClick={() => setAttaching(r)}
                    className={`inline-flex min-h-11 items-center gap-2 rounded-md px-4 text-sm ${l.status === 'new' ? 'border border-hair text-ink' : 'bg-run font-medium text-runink'}`}>
                    <Icon name="world" size={15} />{l.status === 'demo_ready' ? 'Change demo' : 'Attach demo'}</button>
                  <button type="button" disabled={busy === l.id} onClick={() => setDeclining(r)}
                    className="inline-flex min-h-11 items-center rounded-md border border-hair px-4 text-sm text-muted disabled:opacity-50">Decline</button>
                </div>
              )}
            </article>
          );
        })}
      </div>

      <ConfirmDialog open={!!declining} destructive title={`Decline ${declining?.l.businessName ?? ''}?`}
        body="They’ll see “Thanks for asking” and can send the form again later. No email goes out."
        confirmLabel="Decline" onConfirm={() => declining && void move(declining, 'declined')} onCancel={() => setDeclining(null)} />
      {attaching && <AttachDialog lead={attaching} onClose={() => setAttaching(null)} />}
    </>
  );
}

function AttachDialog({ lead, onClose }: { lead: AdminLead; onClose(): void }) {
  const qc = useQueryClient();
  const [v, setV] = useState({
    slug: lead.siteSlug ?? `${slugify(lead.l.businessName)}-demo`, name: lead.l.businessName, previewUrl: '', repoUrl: '',
  });
  const [busy, setBusy] = useState(false);
  const errs = {
    slug: /^[a-z0-9][a-z0-9-]{1,59}$/.test(v.slug) ? null : 'Lowercase letters, numbers and dashes.',
    previewUrl: /^https:\/\/\S+\.\S+/.test(v.previewUrl) ? null : 'An https:// link.',
    repoUrl: /^https:\/\/github\.com\/[\w.-]+\/[\w.-]+$/.test(v.repoUrl) ? null : 'https://github.com/<owner>/<repo>',
  };
  const ok = !errs.slug && !errs.previewUrl && !errs.repoUrl && !!v.name.trim();
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!ok) return;
    setBusy(true);
    try {
      const r = await api.adminAttachDemo(lead.l.id, { slug: v.slug, name: v.name.trim(), previewUrl: v.previewUrl.trim(), repoUrl: v.repoUrl.trim() });
      toast({ kind: 'success', message: r.outboxId ? 'Attached. The “preview ready” email is waiting in the Outbox for you.' : 'Demo updated.' });
      void qc.invalidateQueries({ queryKey: ['admin-leads'] });
      onClose();
    } catch (err) {
      toast({ kind: 'error', message: apiErrorText(err, 'Couldn’t attach that demo.') });
    } finally { setBusy(false); }
  };
  const field = (k: keyof typeof v, label: string, placeholder: string, err: string | null, type = 'text') => (
    <label className="flex flex-col gap-1.5 text-sm">
      <span className="font-medium text-ink">{label}</span>
      <input value={v[k]} onChange={(e) => setV({ ...v, [k]: e.target.value })} placeholder={placeholder} type={type}
        autoCapitalize="none" spellCheck={false}
        className={`min-h-11 w-full min-w-0 rounded-md border bg-bg px-3 text-ink ${v[k] && err ? 'border-danger' : 'border-hair'}`} />
      {v[k] && err && <span className="text-xs text-danger">{err}</span>}
    </label>
  );
  return (
    <Modal open onClose={onClose} title={`Attach a demo: ${lead.l.businessName}`}>
      <form onSubmit={submit} className="flex flex-col gap-3">
        {field('slug', 'Slug', 'pats-roofing-demo', errs.slug)}
        {field('name', 'Name', 'Pat’s Roofing', v.name.trim() ? null : 'A name.')}
        {field('previewUrl', 'Preview URL', 'https://main.d123.amplifyapp.com', errs.previewUrl, 'url')}
        {field('repoUrl', 'Repo URL', 'https://github.com/kc-hansen/pats-roofing', errs.repoUrl, 'url')}
        <p className="text-xs text-muted">The prospect sees it right away. The “preview ready” email waits in the Outbox for you.</p>
        <div className="mt-2 flex justify-end gap-2">
          <button type="button" onClick={onClose} className="min-h-11 rounded-md border border-hair px-4 text-sm text-ink">Cancel</button>
          <button type="submit" disabled={!ok || busy} className="min-h-11 rounded-md bg-run px-4 text-sm font-medium text-runink disabled:opacity-50">
            {busy ? 'Attaching…' : 'Attach demo'}</button>
        </div>
      </form>
    </Modal>
  );
}
